import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OfflineProvider } from './contexts/OfflineContext';

// Importar componentes
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Profile from './components/Profile';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import CeoCreateUser from './components/CeoCreateUser';
import ClientCreate from './components/ClientCreate';
import DemandBoard from './components/DemandBoard';
import SettingsClientAccess from './components/SettingsClientAccess';
import PresentationSettings from './components/PresentationSettings';
import PermissionsManagement from './components/PermissionsManagement';
import WorkSchedulesManagement from './components/WorkSchedulesManagement';
import { useAllowedClientCreateRoles } from './hooks/useClientAccess';
import ClientsList from './components/ClientsList';
import ClientsManagement from './components/ClientsManagement';
import ClientFirstContact from './components/ClientFirstContact';
import Appointments from './components/Appointments';
import AppointmentForm from './components/AppointmentForm';
import Inventory from './components/Inventory';
import StockView from './components/StockView';
import InventoryCategoriesManager from './components/InventoryCategoriesManager';
import FinanceDashboard from './components/FinanceDashboard';
import FinanceEntries from './components/FinanceEntries';
import FinanceExpenses from './components/FinanceExpenses';
import FinancePayroll from './components/FinancePayroll';
import FinancePayrollAnalysis from './components/FinancePayrollAnalysis';
import FinanceExpensesAnalysis from './components/FinanceExpensesAnalysis';
import PayrollIndicatorsConfig from './components/PayrollIndicatorsConfig';
import CostsManagement from './components/CostsManagement';
import CostsManagementEnhanced from './components/CostsManagementEnhanced';
import CategoriesManagement from './components/CategoriesManagement';
import FinanceCategoriesManager from './components/FinanceCategoriesManager';
import SuppliersManagement from './components/SuppliersManagement';
import WarehousesManagement from './components/WarehousesManagement';
import PurchasesList from './components/PurchasesList';
import CostStats from './components/CostStats';
import MaterialCatalogManager from './components/MaterialCatalogManager';
import ProductOptionsManager from './components/ProductOptionsManager';
import ProductsManager from './components/ProductsManager';
import UnitsManager from './components/UnitsManager';
import BrandsManager from './components/BrandsManager';
import CoresManager from './components/CoresManager';
import TamanhosManager from './components/TamanhosManager';
import SmartScheduleConfig from './components/SmartScheduleConfig';
import WaitingList from './components/WaitingList';
import WaitingListScoreConfig from './components/WaitingListScoreConfig';
import PricingConfig from './components/PricingConfig';
import PreProductionConfig from './components/PreProductionConfig';
import Form3DConfig from './components/Form3DConfig';
import SketchProjects from './components/SketchProjects';
import AuditLogs from './components/AuditLogs';
import SurveyListSimple from './components/SurveyListSimple';
import SurveyTest from './components/SurveyTest';
import SurveyFormWrapper from './components/SurveyFormWrapper';
import Form3D from './components/Form3D';
import BudgetSheetForm from './components/BudgetSheetForm';
// import TimeClock from './components/TimeClock'; // DESATIVADO - Controle de ponto removido
import AppointmentSessions from './components/AppointmentSessions';
import EmployeeSalary from './components/EmployeeSalary';
import InssTablesManagement from './components/InssTablesManagement';
import PayslipsManagement from './components/PayslipsManagement';
import WorkedSaturdaysManagement from './components/WorkedSaturdaysManagement';
import OvertimeRecordsManagement from './components/OvertimeRecordsManagement';
import HolidaysManagement from './components/HolidaysManagement';
import AbsencesManagement from './components/AbsencesManagement';
import VacationManagement from './components/VacationManagement';
import ContractManagement from './components/ContractManagement';
import ExtraAdvancesManagement from './components/ExtraAdvancesManagement';
import TaxContributionsManagement from './components/TaxContributionsManagement';
import ExpensesManagement from './components/ExpensesManagement';
import ProfitSharingManagement from './components/ProfitSharingManagement';
import MyPayslips from './components/MyPayslips';
import MyTimeRecords from './components/MyTimeRecords';
// import FinanceOvertimeControl from './components/FinanceOvertimeControl';
import FinanceForecastedExpenses from './components/FinanceForecastedExpenses';
import AccountsPayable from './components/AccountsPayable';
import RecurringExpenseForm from './components/RecurringExpenseForm';
import FinanceForecastedIncomes from './components/FinanceForecastedIncomes';
import AdvanceRequest from './components/AdvanceRequest';
import AdvanceApproval from './components/AdvanceApproval';
import ProjectsList from './components/ProjectsList';
import QuickProjectPage from './components/QuickProjectPage';
import PortfolioBacklogPage from './components/PortfolioBacklogPage';
import ProjectBudget from './components/ProjectBudget';
import ProjectHoursDetail from './components/ProjectHoursDetail.jsx';
import ProjectExecution from './components/ProjectExecution';
import ProductionDashboard from './components/ProductionDashboard';
import RecebimentosDashboard from './components/RecebimentosDashboard';
import ProjectExecutionDetail from './components/ProjectExecutionDetail';
import ProjectSimulator from './components/ProjectSimulator.jsx';
import BudgetsList from './components/BudgetsList';
import CapacityCounter from './components/CapacityCounter';
import Trainings from './components/Trainings';
import PaymentConditionsManager from './components/PaymentConditionsManager';
import FiscalDocuments from './components/FiscalDocuments';
import FiscalCategoriesManager from './components/FiscalCategoriesManager';
import CompanyCashbox from './components/CompanyCashbox';
import CashboxNewTransaction from './components/CashboxNewTransaction';
import CashboxMyTransactions from './components/CashboxMyTransactions';
import PreProjects from './components/PreProjects.jsx';
import BudgetMarginRulesManager from './components/BudgetMarginRulesManager';
import BudgetHistoryStats from './components/BudgetHistoryStats';
import BudgetSheetOptionsManager from './components/BudgetSheetOptionsManager';
import ManualBudgetCreator from './components/ManualBudgetCreator';
import QuickBudget from './components/QuickBudget';
import BudgetPreviewTest from './components/budget/BudgetPreviewTest';
import CuttingPlansList from './components/CuttingPlansList';
import CuttingPlanDetail from './components/CuttingPlanDetail';
import CuttingPlanWizard from './components/CuttingPlanWizard';
import CuttingPlanEditor from './components/CuttingPlanEditor';
import CuttingPlanResult from './components/CuttingPlanResult';
import BoardCutViewer from './components/BoardCutViewer';
import BoardPatternsManager from './components/BoardPatternsManager';
import ProjectModules from './components/ProjectModules';
import ModuleParts from './components/ModuleParts';
import SystemManualsPage from './components/SystemManualsPage';
import LabelBipScannerPage from './pages/LabelBipScannerPage';
import { hasPermission as hasPerm } from './config/permissions';

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

// Componente para rotas protegidas
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">
            Acesso Negado
          </h1>
          <p className="text-secondary-600 mb-4">
            Você não tem permissão para acessar esta página.
          </p>
          <p className="text-sm text-secondary-500">
            Seu nível: <span className="font-semibold">{user?.role}</span>
          </p>
          <p className="text-sm text-secondary-500">
            Requerido: <span className="font-semibold">{requiredRoles.join(', ')}</span>
          </p>
        </div>
      </div>
    );
  }

  return children;
};

// Guarda por permissão
const ProtectedPermission = ({ children, anyPermissions = [] }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (anyPermissions.length > 0) {
    const allowed = anyPermissions.some(p => hasPerm(user?.role, p));
    // Debug log - remover em produção
    // console.log(`[ProtectedPermission] user.role=${user?.role}, anyPermissions=${JSON.stringify(anyPermissions)}, allowed=${allowed}`);
    if (!allowed) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-secondary-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">Acesso negado</h1>
            <p className="text-secondary-600">Você não tem permissão para acessar esta área.</p>
          </div>
        </div>
      );
    }
  }

  return children;
};

// Componente para rotas públicas (redirecionar se autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Componente para proteger a rota de cadastro de cliente
const ClientCreateGuard = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: allowedRoles, isLoading: loadingRoles, isFetching } = useAllowedClientCreateRoles();

  if (isLoading || loadingRoles || isFetching) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (Array.isArray(allowedRoles) && allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">
            Acesso Negado
          </h1>
          <p className="text-secondary-600 mb-4">
            Você não tem permissão para criar novos clientes.
          </p>
          <p className="text-sm text-secondary-500">
            Seu nível: <span className="font-semibold">{user?.role}</span>
          </p>
          <p className="text-sm text-secondary-500">
            Permitidos: <span className="font-semibold">{allowedRoles.join(', ')}</span>
          </p>
        </div>
      </div>
    );
  }

  return children;
};

// Componente principal das rotas
const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Rotas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Rotas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - todos os funcionários autenticados */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Perfil - todos os funcionários */}
        <Route path="profile" element={<Profile />} />

        {/* Demandas Kanban - todos os funcionários */}
        <Route
          path="demands"
          element={<DemandBoard userRole={user?.role} />}
        />

        {/* Funcionários - apenas GERENCIA */}
        <Route
          path="users"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <Users />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Salário dos Funcionários - apenas GERENCIA */}
        <Route
          path="users/salarios"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <EmployeeSalary />
            </ProtectedRoute>
          }
        />

        {/* Faixas INSS - apenas GERENCIA */}
        <Route
          path="users/faixas-inss"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <InssTablesManagement />
            </ProtectedRoute>
          }
        />

        {/* Controle de Sábados Trabalhados - apenas GERENCIA */}
        <Route
          path="users/sabados-trabalhados"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <WorkedSaturdaysManagement />
            </ProtectedRoute>
          }
        />

        {/* Controle de Horas Extras - apenas GERENCIA */}
        <Route
          path="users/horas-extras"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <OvertimeRecordsManagement />
            </ProtectedRoute>
          }
        />

        {/* Gerenciamento de Feriados - apenas GERENCIA */}
        <Route
          path="users/feriados"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <HolidaysManagement />
            </ProtectedRoute>
          }
        />

        {/* Gerenciamento de Divergências/Faltas - apenas GERENCIA */}
        <Route
          path="users/divergencias"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <AbsencesManagement />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Férias - apenas GERENCIA */}
        <Route
          path="users/ferias"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <VacationManagement />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Contratos - apenas GERENCIA */}
        <Route
          path="users/contratos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ContractManagement />
            </ProtectedRoute>
          }
        />

        {/* Área de Vale (Adiantamento Extra) - apenas GERENCIA */}
        <Route
          path="users/vales"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ExtraAdvancesManagement />
            </ProtectedRoute>
          }
        />

        {/* Contribuições Fiscais (INSS/FGTS) - apenas GERENCIA */}
        <Route
          path="users/contribuicoes-fiscais"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <TaxContributionsManagement />
            </ProtectedRoute>
          }
        />

        {/* Despesas Diversas - apenas GERENCIA */}
        <Route
          path="users/despesas-diversas"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ExpensesManagement />
            </ProtectedRoute>
          }
        />

        {/* Participação de Lucros (PLR) - apenas GERENCIA */}
        <Route
          path="users/plr"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProfitSharingManagement />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Holerites - apenas GERENCIA */}
        <Route
          path="payslips"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PayslipsManagement />
            </ProtectedRoute>
          }
        />

        {/* Meus Holerites - todos os funcionários */}
        <Route
          path="meus-holerites"
          element={
            <ProtectedRoute>
              <MyPayslips />
            </ProtectedRoute>
          }
        />

        {/* Meus Registros (Horas Extras + Sábados) - todos os funcionários */}
        <Route
          path="meus-registros"
          element={
            <ProtectedRoute>
              <MyTimeRecords />
            </ProtectedRoute>
          }
        />

        {/* Adiantamento Quinzenal - todos os funcionários */}
        <Route
          path="adiantamento"
          element={
            <ProtectedRoute>
              <AdvanceRequest />
            </ProtectedRoute>
          }
        />

        {/* Aprovação de Adiantamentos - apenas GERENCIA */}
        <Route
          path="adiantamento/aprovacao"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <AdvanceApproval />
            </ProtectedRoute>
          }
        />

        {/* Novo Funcionário - apenas CEO */}
        <Route
          path="ceo/novo-funcionario"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <CeoCreateUser />
            </ProtectedRoute>
          }
        />

        {/* Cadastro de Cliente - autorizado no backend conforme settings */}
        <Route
          path="clientes/novo"
          element={
            <ProtectedRoute>
              <ClientCreateGuard>
                <ClientCreate />
              </ClientCreateGuard>
            </ProtectedRoute>
          }
        />

        {/* Gestão de Clientes - autorizado conforme settings (mesma permissão da criação) */}
        <Route
          path="clientes"
          element={
            <ProtectedRoute>
              <ClientCreateGuard>
                <ClientsManagement />
              </ClientCreateGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="primeiro-contato"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']}>
              <ClientFirstContact />
            </ProtectedRoute>
          }
        />

        {/* Configuração de acesso à criação de clientes - apenas CEO */}
        <Route
          path="config/clientes-acesso"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <SettingsClientAccess />
            </ProtectedRoute>
          }
        />

        {/* Configurações de Apresentação - apenas GERENCIA */}
        <Route
          path="config/apresentacao"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PresentationSettings />
            </ProtectedRoute>
          }
        />

        {/* Gerenciamento de Permissões - apenas GERENCIA */}
        <Route
          path="config/permissoes"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PermissionsManagement />
            </ProtectedRoute>
          }
        />

        {/* Gerenciamento de Jornadas de Trabalho - apenas GERENCIA */}
        <Route
          path="config/jornadas"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <WorkSchedulesManagement />
            </ProtectedRoute>
          }
        />

        {/* Controle de Ponto - DESATIVADO */}
        {/* <Route
          path="ponto"
          element={
            <ProtectedRoute>
              <TimeClock />
            </ProtectedRoute>
          }
        /> */}

        {/* Controle de Atendimentos - todos os funcionários */}
        <Route
          path="atendimentos"
          element={
            <ProtectedRoute>
              <AppointmentSessions />
            </ProtectedRoute>
          }
        />

        {/* Funcionalidades específicas por role */}
        <Route
          path="admin/*"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-4">Área Administrativa</h1>
                <p>Funcionalidades administrativas para CEO e Gerente</p>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="operacional/*"
          element={
            <ProtectedRoute requiredRoles={['PRODUCAO', 'GERENCIA']}>
              <div className="p-6">
                <h1 className="text-2xl font-bold mb-4">Área Operacional</h1>
                <p>Funcionalidades operacionais para Técnico e Gerente</p>
              </div>
            </ProtectedRoute>
          }
        />

        {/* Agendamentos - todos exceto PRODUCAO e MONTAGEM_CLIENTE */}
        <Route
          path="agendamentos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']}>
              <Appointments />
            </ProtectedRoute>
          }
        />

        {/* Novo Agendamento - todos exceto PRODUCAO e MONTAGEM_CLIENTE */}
        <Route
          path="agendamentos/novo"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']}>
              <AppointmentForm />
            </ProtectedRoute>
          }
        />

        {/* Editar Agendamento - todos exceto PRODUCAO e MONTAGEM_CLIENTE */}
        <Route
          path="agendamentos/editar/:id"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']}>
              <AppointmentForm />
            </ProtectedRoute>
          }
        />

        {/* Estoque - setores com permissão */}
        <Route
          path="estoque"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'PRODUCAO', 'MONTAGEM_CLIENTE']}>
              <Inventory />
            </ProtectedRoute>
          }
        />

        {/* Visualização de Estoque - setores com permissão */}
        <Route
          path="estoque/visualizar"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'PRODUCAO', 'MONTAGEM_CLIENTE']}>
              <StockView />
            </ProtectedRoute>
          }
        />

        {/* Categorias de Produtos/Estoque */}
        <Route
          path="estoque/categorias"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <InventoryCategoriesManager />
            </ProtectedRoute>
          }
        />

        {/* Leitura e Bipagem de Etiquetas / QR Code de Peças e Estoque */}
        <Route
          path="etiqueta/:code"
          element={
            <ProtectedRoute>
              <LabelBipScannerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="inventory/stock/:estoqueId/label/:code"
          element={
            <ProtectedRoute>
              <LabelBipScannerPage />
            </ProtectedRoute>
          }
        />

        {/* Financeiro */}
        <Route
          path="financeiro"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view', 'financial:expenses']}>
              <FinanceDashboard />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/entradas"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view', 'financial:expenses']}>
              <FinanceEntries />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/saidas"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view', 'financial:expenses']}>
              <FinanceExpenses />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/saidas/analise"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view', 'financial:expenses']}>
              <FinanceExpensesAnalysis />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/saidas-previstas"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <FinanceForecastedExpenses />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/contas-a-pagar"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <AccountsPayable />
            </ProtectedPermission>
          }
        />
        <Route
          path="contas-a-pagar"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <AccountsPayable />
            </ProtectedPermission>
          }
        />
        <Route
          path="despesas/lancar"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:expenses']}>
              <RecurringExpenseForm />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/entradas-previstas"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <FinanceForecastedIncomes />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/folha"
          element={
            <ProtectedPermission anyPermissions={['financial:full']}>
              <FinancePayroll />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/folha/analise"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <FinancePayrollAnalysis />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/caixa-empresa"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <CompanyCashbox />
            </ProtectedRoute>
          }
        />
        {/* Nova Transação - apenas RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="financeiro/nova-transacao"
          element={
            <ProtectedRoute requiredRoles={['RECEPCAO_COMPRAS_AGENDA']}>
              <CashboxNewTransaction />
            </ProtectedRoute>
          }
        />
        {/* Minhas Transações - apenas RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="financeiro/minhas-transacoes"
          element={
            <ProtectedRoute requiredRoles={['RECEPCAO_COMPRAS_AGENDA']}>
              <CashboxMyTransactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="financeiro/folha/indicadores"
          element={
            <ProtectedPermission anyPermissions={['financial:full']}>
              <PayrollIndicatorsConfig />
            </ProtectedPermission>
          }
        />
        <Route
          path="financeiro/categorias"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <FinanceCategoriesManager />
            </ProtectedPermission>
          }
        />
        {/* Controle de Horas Extras - Painel Financeiro (desativado - usa sistema de ponto) */}
        {/* <Route
          path="financeiro/horas-extras"
          element={
            <ProtectedPermission anyPermissions={['financial:full', 'financial:view']}>
              <FinanceOvertimeControl />
            </ProtectedPermission>
          }
        /> */}

        {/* Custos - apenas GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="custos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CostsManagementEnhanced />
            </ProtectedRoute>
          }
        />

        {/* Catálogo de Materiais - apenas GERENCIA */}
        <Route
          path="custos/catalogo"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <MaterialCatalogManager />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Produtos - apenas GERENCIA */}
        <Route
          path="produtos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProductsManager />
            </ProtectedRoute>
          }
        />

        {/* Opções Flexíveis de Produtos - apenas GERENCIA */}
        <Route
          path="produtos/opcoes"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProductOptionsManager />
            </ProtectedRoute>
          }
        />

        {/* Unidades de Medida - apenas GERENCIA */}
        <Route
          path="produtos/unidades"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <UnitsManager />
            </ProtectedRoute>
          }
        />

        {/* Marcas - apenas GERENCIA */}
        <Route
          path="produtos/marcas"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <BrandsManager />
            </ProtectedRoute>
          }
        />

        {/* Cores - apenas GERENCIA */}
        <Route
          path="produtos/cores"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <CoresManager />
            </ProtectedRoute>
          }
        />

        {/* Tamanhos - apenas GERENCIA */}
        <Route
          path="produtos/tamanhos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <TamanhosManager />
            </ProtectedRoute>
          }
        />

        {/* Condições de Pagamento - apenas GERENCIA */}
        <Route
          path="condicoes-pagamento"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PaymentConditionsManager onClose={() => window.history.back()} />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Fornecedores - apenas GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="fornecedores"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <SuppliersManagement />
            </ProtectedRoute>
          }
        />

        {/* Gestão de Armazéns - apenas GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="armazens"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <WarehousesManagement />
            </ProtectedRoute>
          }
        />

        {/* Entradas de Compra - desativado, redireciona para estoque */}
        <Route
          path="compras"
          element={<Navigate to="/estoque" replace />}
        />

        {/* Estatísticas de Custos - apenas GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="custos/estatisticas"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CostStats />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Agenda de Levantamento - apenas GERENCIA */}
        <Route
          path="config/agenda-levantamento"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <SmartScheduleConfig />
            </ProtectedRoute>
          }
        />

        {/* Fila de Espera - GERENCIA, RECEPCAO_COMPRAS_AGENDA e RECEPCAO_PROJETOS */}
        <Route
          path="config/fila-espera"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA', 'RECEPCAO_PROJETOS']}>
              <WaitingList />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Scores da Fila de Espera - apenas GERENCIA */}
        <Route
          path="config/fila-espera/scores"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <WaitingListScoreConfig />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Preços - apenas GERENCIA */}
        <Route
          path="config/precos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PricingConfig />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Pré-Produção - apenas GERENCIA */}
        <Route
          path="config/pre-producao"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <PreProductionConfig />
            </ProtectedRoute>
          }
        />

        {/* Configuração de Acabamento 3D - apenas GERENCIA */}
        <Route
          path="config/acabamento-3d"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <Form3DConfig />
            </ProtectedRoute>
          }
        />

        {/* Levantamentos - GERENCIA, RECEPCAO_PROJETOS e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="levantamentos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_PROJETOS', 'RECEPCAO_COMPRAS_AGENDA']}>
              <SurveyListSimple />
            </ProtectedRoute>
          }
        />

        {/* Novo Levantamento a partir de Agendamento - GERENCIA */}
        <Route
          path="levantamentos/novo"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <SurveyFormWrapper />
            </ProtectedRoute>
          }
        />

        {/* Formulário de Acabamento 3D - apenas RECEPCAO_PROJETOS */}
        <Route
          path="levantamentos/:surveyId/form-3d"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_PROJETOS']}>
              <Form3D />
            </ProtectedRoute>
          }
        />

        {/* Ficha de Orçamento - GERENCIA e RECEPCAO_PROJETOS */}
        <Route
          path="levantamentos/:surveyId/ficha-orcamento"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_PROJETOS']}>
              <BudgetSheetForm />
            </ProtectedRoute>
          }
        />

        {/* Teste de Levantamentos - Temporário */}
        <Route
          path="teste-levantamentos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_PROJETOS']}>
              <SurveyTest />
            </ProtectedRoute>
          }
        />

        {/* Auditoria - Apenas GERENCIA */}
        <Route
          path="auditoria"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <AuditLogs />
            </ProtectedRoute>
          }
        />

        {/* Pré-Projetos - GERENCIA e RECEPCAO_PROJETOS */}
        <Route
          path="pre-projetos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_PROJETOS']}>
              <PreProjects />
            </ProtectedRoute>
          }
        />

        {/* Croquis 2D - apenas GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="croquis"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <SketchProjects />
            </ProtectedRoute>
          }
        />

        {/* Projetos - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="projetos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <ProjectsList />
            </ProtectedRoute>
          }
        />

        {/* Projeto Rápido - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="projeto-rapido"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <QuickProjectPage />
            </ProtectedRoute>
          }
        />

        {/* Planos de Corte - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlansList />
            </ProtectedRoute>
          }
        />

        {/* Editor de Plano de Corte - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/new"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlanEditor />
            </ProtectedRoute>
          }
        />

        {/* Wizard de Plano de Corte - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/wizard"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlanWizard />
            </ProtectedRoute>
          }
        />

        {/* Editor de Plano de Corte (edição) */}
        <Route
          path="cutting-plans/:id/edit"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlanEditor />
            </ProtectedRoute>
          }
        />

        {/* Resultado do Cálculo - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/:id/result"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlanResult />
            </ProtectedRoute>
          }
        />

        {/* Detalhe do Plano de Corte - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/:id"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <CuttingPlanDetail />
            </ProtectedRoute>
          }
        />

        {/* Módulos do Plano de Corte - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/:id/modules"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <ProjectModules />
            </ProtectedRoute>
          }
        />

        {/* Peças do Módulo - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="modules/:moduleId/parts"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <ModuleParts />
            </ProtectedRoute>
          }
        />

        {/* Visualização de Corte da Chapa - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="cutting-plans/:planId/boards/:boardId"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <BoardCutViewer />
            </ProtectedRoute>
          }
        />

        {/* Padrões de Chapas - GERENCIA e RECEPCAO_COMPRAS_AGENDA */}
        <Route
          path="board-patterns"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'RECEPCAO_COMPRAS_AGENDA']}>
              <BoardPatternsManager />
            </ProtectedRoute>
          }
        />

        {/* Orçamento do Projeto - apenas GERENCIA */}
        <Route
          path="projetos/:projectId/orcamento"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProjectBudget />
            </ProtectedRoute>
          }
        />

        {/* Horas do Projeto - apenas GERENCIA */}
        <Route
          path="projetos/:projectId/horas"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProjectHoursDetail />
            </ProtectedRoute>
          }
        />

        {/* Execução do Projeto - Workflow e Detalhes */}
        <Route
          path="projetos/:projectId/execucao"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'PRODUCAO', 'MONTAGEM_CLIENTE']}>
              <ProjectExecution />
            </ProtectedRoute>
          }
        />

        {/* Projetos em Carteira (PCP & Backlog) */}
        <Route
          path="carteira-projetos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN', 'CEO']}>
              <PortfolioBacklogPage />
            </ProtectedRoute>
          }
        />

        {/* Painel de Produção e Montagem */}
        <Route
          path="producao"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'PRODUCAO', 'MONTAGEM_CLIENTE']}>
              <ProductionDashboard />
            </ProtectedRoute>
          }
        />

        {/* Recebimentos - apenas GERENCIA */}
        <Route
          path="financeiro/recebimentos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <RecebimentosDashboard />
            </ProtectedRoute>
          }
        />

        {/* Detalhes de Execução do Projeto */}
        <Route
          path="projetos/:projectId/execucao-detalhes"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'PRODUCAO', 'MONTAGEM_CLIENTE']}>
              <ProjectExecutionDetail />
            </ProtectedRoute>
          }
        />

        {/* Tela de Orçamentos - apenas GERENCIA */}
        <Route
          path="orcamentos"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <BudgetsList />
            </ProtectedRoute>
          }
        />

        {/* Criar/Editar Orçamento Manual */}
        <Route
          path="orcamentos/criar"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN']}>
              <ManualBudgetCreator />
            </ProtectedRoute>
          }
        />
        <Route
          path="orcamentos/:budgetId/editar"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN']}>
              <ManualBudgetCreator />
            </ProtectedRoute>
          }
        />

        {/* Orçamento Rápido */}
        <Route
          path="quick-budget"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN', 'VENDAS']}>
              <QuickBudget />
            </ProtectedRoute>
          }
        />

        {/* Teste de PDF Premium do Orçamento */}
        <Route
          path="budget-preview-test"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN', 'VENDAS']}>
              <BudgetPreviewTest />
            </ProtectedRoute>
          }
        />

        {/* Regras de Margem de Orçamento - apenas GERENCIA e ADMIN */}
        <Route
          path="budget-margin-rules"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN']}>
              <BudgetMarginRulesManager />
            </ProtectedRoute>
          }
        />

        {/* Histórico de Orçamento - apenas GERENCIA e ADMIN */}
        <Route
          path="budget-history"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN']}>
              <BudgetHistoryStats />
            </ProtectedRoute>
          }
        />

        {/* Opções de Orçamento - apenas GERENCIA e ADMIN */}
        <Route
          path="budget-options"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA', 'ADMIN']}>
              <BudgetSheetOptionsManager />
            </ProtectedRoute>
          }
        />

        {/* Contador de Capacidade - apenas GERENCIA */}
        <Route
          path="capacidade"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <CapacityCounter />
            </ProtectedRoute>
          }
        />

        {/* Simulador de Prazo de Entrega - apenas GERENCIA */}
        <Route
          path="capacidade/simulador"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <ProjectSimulator />
            </ProtectedRoute>
          }
        />

        {/* Treinamentos - todos podem ver */}
        <Route
          path="treinamentos"
          element={
            <ProtectedRoute>
              <Trainings />
            </ProtectedRoute>
          }
        />

        {/* Manuais e Documentação do Sistema - todos podem ver */}
        <Route
          path="manuais"
          element={
            <ProtectedRoute>
              <SystemManualsPage />
            </ProtectedRoute>
          }
        />

        {/* Documentos Fiscais - apenas GERENCIA */}
        <Route
          path="documentos-fiscais"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <FiscalDocuments />
            </ProtectedRoute>
          }
        />

        {/* Gerenciar Categorias de Doc. Fiscais - apenas GERENCIA */}
        <Route
          path="documentos-fiscais/categorias"
          element={
            <ProtectedRoute requiredRoles={['GERENCIA']}>
              <FiscalCategoriesManager />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Rota 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-secondary-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-secondary-900 mb-4">404</h1>
              <p className="text-secondary-600 mb-4">Página não encontrada</p>
              <a
                href="/dashboard"
                className="btn-primary"
              >
                Voltar ao Dashboard
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

// Componente principal da aplicação
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <OfflineProvider>
            <div className="App">
              <AppRoutes />

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#10b981',
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
          </div>
          </OfflineProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App; 